/**
 * The wiring behind „Do przejrzenia": the queue, the submission the trainer has opened, and the one
 * write.
 *
 * ── ONE queue, since 17 September 2026 ───────────────────────────────────────────────────────
 * The three-way segment above the list („Wszystkie / Check-iny / Wstępne") was removed on the
 * owner's word, so this hook takes no argument and the read names no kind: the backend answers with
 * every submission waiting. The `emptyKind` state went with it — „the queue is empty" is now the
 * only empty there is, and it is good news rather than a window with nothing in it.
 *
 * ── The submission opens in a MODAL, and this hook holds only which one ──────────────────────
 * Owner, 17 September 2026. The right-hand pane and its `GET /survey/assignment/{id}` are gone:
 * `features/survey-report`'s dialog draws the client detail's own report, from the client's own
 * history, so the deltas, the units and the media that pane could not show are all there. What is
 * left here is the OPENED submission — a row, or `null` — because the dialog needs the client and
 * the assignment together and the queue row is where both are known.
 *
 * ── The EXPOSED state is the wait branch, tail and all ───────────────────────────────────────
 * `listState` says „pending" for as long as the gate says the wait belongs on screen, which outlasts
 * the read by the skeleton's 300ms minimum life. The screen forks on it and so forks on the gate,
 * and the rows that replace the bars carry the reveal in their first render. `queue.isPending` is
 * the read and stays below, unexposed.
 *
 * ── The mark is `features/survey-review`'s, unchanged ────────────────────────────────────────
 * Same hook the client detail uses, given this queue's one key.
 *
 * ── The write makes the focused row DISAPPEAR, so focus is moved before it does ──────────────
 * A reviewed row leaves the list. `markReviewed` runs its callback before the refetch, which is
 * where the next row is focused and the sentence is published — otherwise the tick the trainer
 * pressed is destroyed under their finger and focus falls to `<body>`.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useMarkSurveyReviewed } from '~/features/survey-review';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import { reviewQueueQueryKey, reviewQueueQueryOptions } from '../api/reportsQueries';
import {
  reviewQueue,
  windowSentence,
  type ReviewQueueView,
  type ReviewRow,
} from '../model/reviewQueue';

/** What the list is showing. */
export type ReviewListState = 'pending' | 'error' | 'empty' | 'rows';

export interface ReviewTabView {
  /** What the list draws. „pending" covers the whole gated wait, not only the open read. */
  readonly listState: ReviewListState;
  readonly listSkeleton: boolean;
  readonly listReveal: string;
  readonly queue: ReviewQueueView;
  /** „Lista sięga 30 dni wstecz…", from the response's own field. `null` before it answers. */
  readonly windowNote: string | null;
  /** The submission the report dialog is open on. `null` is closed. */
  readonly opened: ReviewRow | null;
  readonly onOpen: (assignmentId: number) => void;
  readonly onCloseReport: () => void;
  readonly markReviewed: (assignmentId: number, onMarked?: () => void) => void;
  readonly marking: boolean;
  readonly markFailed: boolean;
  readonly onRetry: () => void;
}

export function useReviewTab(): ReviewTabView {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  /** Which submission the dialog is open on. `null` is closed — no row is „current" otherwise. */
  const [openedId, setOpenedId] = useState<number | null>(null);

  const queue = useQuery(reviewQueueQueryOptions());
  const view = reviewQueue(queue.data, { t, locale });

  // Resolved against the list rather than remembered: a submission marked reviewed leaves the
  // queue, and the dialog it was opened from closes with it rather than describing a row the
  // server no longer returns.
  const opened = view.rows.find((row) => row.assignmentId === openedId) ?? null;

  const listAnswer: ReviewListState = queue.isPending
    ? 'pending'
    : queue.isError
      ? 'error'
      : view.rows.length > 0
        ? 'rows'
        : 'empty';
  const listGate = useSkeletonGate(listAnswer === 'pending');
  const listState: ReviewListState = listGate.waiting ? 'pending' : listAnswer;

  const review = useMarkSurveyReviewed({ invalidates: [reviewQueueQueryKey] });

  return {
    listState,
    listSkeleton: listGate.skeleton,
    listReveal: listGate.reveal,
    queue: view,
    windowNote: windowSentence(view, t),
    opened,
    onOpen: setOpenedId,
    onCloseReport: () => {
      setOpenedId(null);
    },
    markReviewed: review.markReviewed,
    marking: review.marking,
    markFailed: review.markFailed,
    onRetry: () => {
      void queue.refetch();
    },
  };
}
